import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, AlertTriangle, XCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Context for sharing usage status across components
const UsageContext = createContext(null);

export const useUsageStatus = () => {
  const context = useContext(UsageContext);
  return context || { usage: null, limitReached: false, warning: false };
};

export const UsageProvider = ({ children }) => {
  const { token } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/credits`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();

        // Anteil verbraucht = 1 - verfügbar / Monatsmenge. Gekaufte und geschenkte Credits erhöhen "verfügbar" und
        // verhindern damit eine falsche Warnung. Ab 80 % Warnung, bei 0 gesperrt.
        const total = Number(data.credits_total || 0);
        const available = Number(data.credits_available || 0);
        const percentUsed = total > 0 ? Math.max(0, Math.min(100, (1 - available / total) * 100)) : 0;

        setUsage({
          usage: data.credits_used,
          limit: data.credits_total,
          remaining: available,
          percentUsed,
          resetDate: data.credits_reset_date,
          plan: data.plan,
          warning: percentUsed >= 80 && available > 0,
          limitReached: available <= 0,
        });
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsage();
    
    // Polling every 5 seconds
    const interval = setInterval(fetchUsage, 5000);
    return () => clearInterval(interval);
  }, [fetchUsage]);

  const value = {
    usage,
    loading,
    limitReached: usage?.limitReached || false,
    warning: usage?.warning || false,
    refetch: fetchUsage
  };

  return (
    <UsageContext.Provider value={value}>
      {children}
    </UsageContext.Provider>
  );
};

export const UsageStatus = () => {
  const { usage, loading } = useUsageStatus();

  if (loading || !usage) {
    return null;
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
    return num.toString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
    } catch {
      return '';
    }
  };

  const getStatusColor = () => {
    if (usage.limitReached) return 'text-red-500';
    if (usage.warning) return 'text-amber-500';
    return 'text-[#00D9C8]';
  };

  const getProgressColor = () => {
    if (usage.limitReached) return 'bg-red-500';
    if (usage.warning) return 'bg-amber-500';
    return 'bg-[#00D9C8]';
  };

  const getBgColor = () => {
    if (usage.limitReached) return 'bg-red-500/10 border-red-500/30';
    if (usage.warning) return 'bg-amber-500/10 border-amber-500/30';
    return 'bg-[#0A1628]/50';
  };

  const getIcon = () => {
    if (usage.limitReached) return <XCircle className={`w-3.5 h-3.5 ${getStatusColor()}`} />;
    if (usage.warning) return <AlertTriangle className={`w-3.5 h-3.5 ${getStatusColor()}`} />;
    return <Zap className={`w-3.5 h-3.5 ${getStatusColor()}`} />;
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.div 
            className={`px-3 py-2 rounded-lg cursor-pointer transition-colors border ${getBgColor()} hover:opacity-80`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="usage-status"
          >
            <div className="flex items-center gap-2 mb-1">
              {getIcon()}
              <span className="text-xs text-[#A0AEC0]">Credits</span>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full h-1.5 bg-[#1A2B42] rounded-full overflow-hidden">
              <motion.div 
                className={`h-full ${getProgressColor()} rounded-full`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(usage.percentUsed, 100)}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
            
            {/* Usage Numbers */}
            <div className="flex justify-between items-center mt-1">
              <span className={`text-xs font-medium ${getStatusColor()}`}>
                {formatNumber(usage.usage)}
              </span>
              <span className="text-xs text-[#5A6C7D]">
                / {formatNumber(usage.limit)}
              </span>
            </div>

            {/* Warning/Error Badge */}
            <AnimatePresence>
              {usage.limitReached && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2 text-[10px] text-red-400 font-medium text-center"
                >
                  Limit erreicht
                </motion.div>
              )}
              {usage.warning && !usage.limitReached && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2 text-[10px] text-amber-400 font-medium text-center"
                >
                  80% verbraucht
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </TooltipTrigger>
        <TooltipContent 
          side="right" 
          className="bg-[#1A2B42] border-[#0A1628] text-white p-3 max-w-xs"
        >
          <div className="space-y-2">
            <p className="font-medium">Credit-Verbrauch</p>
            <div className="text-sm text-[#A0AEC0] space-y-1">
              <p>Verbraucht: {usage.usage.toLocaleString('de-DE')} Credits</p>
              <p>Limit: {usage.limit.toLocaleString('de-DE')} Credits/Monat</p>
              <p>Verbleibend: {usage.remaining.toLocaleString('de-DE')} Credits</p>
              {usage.plan && (
                <p className="capitalize text-[#00D9C8]">Plan: {usage.plan}</p>
              )}
              {usage.resetDate && (
                <p className="text-[#5A6C7D]">
                  Reset: {formatDate(usage.resetDate)}
                </p>
              )}
            </div>
            {usage.warning && !usage.limitReached && (
              <p className="text-amber-500 text-xs mt-2 border-t border-[#0A1628] pt-2">
                Du hast 80% deiner monatlichen Credits verbraucht.
              </p>
            )}
            {usage.limitReached && (
              <p className="text-red-500 text-xs mt-2 border-t border-[#0A1628] pt-2">
                Credits aufgebraucht! Du kannst bis zum Reset keinen neuen Content generieren.
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default UsageStatus;
